/**
 * Sidecart
 * Slide-in cart drawer for the storefront. Handles:
 *   - Open/close (cart icon trigger, backdrop click, Esc key)
 *   - Quantity +/- and line item removal
 *   - Subscription (selling plan) toggle and plan selection
 *   - Promo / discount code apply & remove
 *   - Subtotal breakdown toggle, SMS opt-in toggle
 *   - Product quick-view modal close → open/refresh (standard PDP ATC
 *     submissions are owned by assets/product-form.js, which calls
 *     window.Sidecart.open()/.refresh() on success — see below)
 *   - Section refresh via ?section_id and header count sync
 *   - Cross-sell carousel init (Swiper)
 *
 * Public API (used by product-form.js and layout/theme.liquid):
 *   - window.Sidecart.open() / .close() / .refresh()
 *
 * External dependencies:
 *   - Swiper (global) — initialized inside the drawer after each refresh
 *   - window.Shopify.routes.root — for /cart/* endpoints
 *
 * Custom events listened to:
 *   - 'cartUpdated'  — theme-wide cart change signal (see CRO.md)
 *   - 'cart:refresh' — explicit refresh signal from other components
 *   - 'xsell:added'  — fired by component-cart-xsell.js after a successful add
 *
 * Required DOM hooks:
 *   - #sidecart-drawer            — root drawer element
 *   - #cart-icon-bubble           — header cart trigger
 *   - form[action*="/cart/add"]   — only the quick-view modal form is watched
 *     here; the standard PDP form's submit is handled by product-form.js
 *
 * [DEV: section id is read from #sidecart-drawer[data-section-id]]
 * [DEV: verify TRIGGER_SELECTOR matches the cart icon in the theme header (CRO.md: #cart-icon-bubble)]
 */

(function () {
  'use strict';

  // ─── Configuration ────────────────────────────────────────────────────────
  const DEFAULT_SECTION_ID = 'sidecart';
  const APP_BLOCKS_SEL = '.sidecart__app-blocks';
  const TRIGGER_SEL = '#cart-icon-bubble';
  const ATC_FORM_SEL = 'form[action*="/cart/add"]';
  const DRAWER_ID = 'sidecart-drawer';
  const OPEN_CLASS = 'sidecart--active';
  const LOCK_CLASS = 'sidecart-open';
  const SWIPER = '.js-xsell-swiper';
  const PREV = '.js-xsell-prev';
  const NEXT = '.js-xsell-next';
  const BENEFIT_ACCORDION = '.sidecart__benefits-accordion';
  // Must match the CSS transition duration of .sidecart__promo-field--open
  const PROMO_FIELD_TRANSITION_MS = 310;

  // ─── Module state ─────────────────────────────────────────────────────────
  let drawerEl = null;
  let isOpen   = false;
  // True while a successful promo apply is showing its feedback message and waiting
  // for the delayed refresh. Blocks the global `cartUpdated` listener from refreshing
  // the drawer early and wiping the success message before the user can read it.
  let promoRefreshPending = false;
  // Handle for the auto-hide timer of an error feedback message. Tracked at module
  // scope so a rapid second apply can cancel the previous pending hide.
  let promoFeedbackTimer = null;
  let liveAppBlocks = null;

  // ─── DOM helpers ──────────────────────────────────────────────────────────

  /** Lazily resolves and caches the drawer root element. */
  function getDrawer() {
    if (!drawerEl) drawerEl = document.getElementById(DRAWER_ID);
    return drawerEl;
  }

  /**
   * Initializes the cross-sell Swiper carousel inside the drawer.
   * Safe no-op if Swiper isn't loaded or the carousel markup is absent.
   */
  function initSwiper(wrapper) {
    const el = wrapper.querySelector(SWIPER);
    if (!el || typeof Swiper === 'undefined') return;

    const xsellWrap = wrapper.querySelector('.cart-xsell');
    const prevBtn   = wrapper.querySelector(PREV);
    const nextBtn   = wrapper.querySelector(NEXT);

    new Swiper(el, {
      slidesPerView: 3.2,
      spaceBetween: 10,
      loop: false,
      autoHeight: false,
      navigation: { prevEl: prevBtn, nextEl: nextBtn },
      on: {
        init: function () {
          if (xsellWrap) xsellWrap.classList.remove('cart-xsell--loading');
        },
      },
    });
  }

  // ─── Drawer open / close ──────────────────────────────────────────────────

  /** Opens the drawer, locks body scroll, and focuses the close button. */
  function open() {
    const el = getDrawer();
    if (!el) return;
    el.classList.add(OPEN_CLASS);
    el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add(LOCK_CLASS);
    isOpen = true;
    mountAppBlocks(el);
    const closeBtn = el.querySelector('.sidecart__close');
    if (closeBtn) closeBtn.focus();
  }

  /** Closes the drawer, restores scroll, and returns focus to the trigger. */
  function close() {
    const el = getDrawer();
    if (!el) return;
    el.classList.remove(OPEN_CLASS);
    el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove(LOCK_CLASS);
    isOpen = false;
    hideCarbonTooltip();
    const trigger = document.querySelector(TRIGGER_SEL);
    if (trigger) trigger.focus();
  }

  // ─── Cart operations ──────────────────────────────────────────────────────

  function getSectionId() {
    const el = getDrawer();
    const fromDom = el && el.getAttribute('data-section-id');
    return fromDom || DEFAULT_SECTION_ID;
  }

  function syncAppBlocksPlacement(drawer) {
    if (!liveAppBlocks) return;
    const hasItems = Boolean(drawer.querySelector('.sidecart__items'));
    liveAppBlocks.classList.toggle('sidecart__app-blocks--parked', !hasItems);
    liveAppBlocks.setAttribute('aria-hidden', hasItems ? 'false' : 'true');
    if (!hasItems) return;
    const jq = window.jQuery;
    if (jq && jq.fn && jq.fn.slick) {
      try {
        jq(liveAppBlocks).find('.slick-initialized').slick('setPosition');
      } catch (err) { /* slider may not be ready yet */ }
    }
  }

  let carbonTooltip = null;
  let carbonTooltipBtn = null;
  let carbonTooltipPinned = false;

  var CARBON_COPY = {
    title: 'Regenerate it.',
    subheading: "Give 1% and we'll match it to support local farmers transition to regenerative practices.",
    description: 'Our regenerative agriculture projects - in partnership with Zero Foodprint, a James Beard Humanitarian of the Year Recipient - are rooted in a powerful idea: farming can be one of the most effective tools we have in the fight against climate change.'
  };

  function setCarbonText(el, value) {
    if (!el || el.textContent.trim() === value) return;
    el.textContent = value;
  }

  /**
   * Adds the info control from the sidecart design onto each Carbon card title
   * and replaces the app copy with the design copy. The long description stays
   * hidden and is shown in the floating tooltip.
   */
  function applyCarbonCopy(title) {
    const card = title.closest('.carbonblock-card');
    if (!card) return;

    let textNode = null;
    for (let i = 0; i < title.childNodes.length; i++) {
      if (title.childNodes[i].nodeType === 3 && title.childNodes[i].nodeValue.trim()) {
        textNode = title.childNodes[i];
        break;
      }
    }
    if (!textNode) {
      textNode = document.createTextNode('');
      title.insertBefore(textNode, title.firstChild);
    }
    if (textNode.nodeValue.trim() !== CARBON_COPY.title) textNode.nodeValue = CARBON_COPY.title;

    setCarbonText(card.querySelector('.block_text_subheading'), CARBON_COPY.subheading);

    const overlay = card.querySelector('.description-overlay');
    if (!overlay) return;
    let description = overlay.querySelector('.block_description');
    if (!description) {
      description = document.createElement('p');
      description.className = 'block_description';
      overlay.appendChild(description);
    }
    setCarbonText(description, CARBON_COPY.description);
    const extras = overlay.querySelectorAll('.block_description');
    for (let i = 1; i < extras.length; i++) extras[i].remove();
  }

  function enhanceCarbonBlock(title) {
    applyCarbonCopy(title);
    if (title.querySelector('.carbonblock-info')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'carbonblock-info';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-label', 'More about this contribution');
    btn.setAttribute('aria-describedby', 'carbonblock-tooltip');
    title.appendChild(btn);
  }

  function enhanceCarbonBlocks(root) {
    if (!root) return;
    const titles = root.querySelectorAll('.sidecart__app-blocks .block_text');
    for (let i = 0; i < titles.length; i++) enhanceCarbonBlock(titles[i]);
  }

  function getCarbonTooltip() {
    const drawer = getDrawer();
    if (!drawer) return null;
    if (carbonTooltip && drawer.contains(carbonTooltip)) return carbonTooltip;
    const tip = document.createElement('div');
    tip.id = 'carbonblock-tooltip';
    tip.className = 'carbonblock-tooltip';
    tip.setAttribute('role', 'tooltip');
    tip.hidden = true;
    const text = document.createElement('p');
    text.className = 'carbonblock-tooltip__text';
    tip.appendChild(text);
    drawer.appendChild(tip);
    carbonTooltip = tip;
    return tip;
  }

  function carbonDescriptionText(btn) {
    const card = btn.closest('.carbonblock-card');
    if (!card) return '';
    const parts = card.querySelectorAll('.block_description');
    const chunks = [];
    for (let i = 0; i < parts.length; i++) {
      const value = parts[i].textContent.trim();
      if (value) chunks.push(value);
    }
    return chunks.join(' ');
  }

  function placeCarbonTooltip(btn, tip) {
    const drawer = getDrawer();
    const panel = drawer && drawer.querySelector('.sidecart__panel');
    if (!drawer || !panel) return;
    const btnRect = btn.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const drawerRect = drawer.getBoundingClientRect();
    const width = Math.min(312, Math.max(160, panelRect.width - 24));
    tip.style.width = width + 'px';
    const height = tip.offsetHeight;
    const arrowOffset = 110;
    let left = btnRect.left + (btnRect.width / 2) - arrowOffset;
    const minLeft = panelRect.left + 12;
    const maxLeft = panelRect.right - width - 12;
    left = Math.max(minLeft, Math.min(left, maxLeft));
    let top = btnRect.top - height - 16;
    const minTop = Math.max(panelRect.top + 8, 8);
    if (top < minTop) top = minTop;
    tip.style.left = (left - drawerRect.left) + 'px';
    tip.style.top = (top - drawerRect.top) + 'px';
    const arrowX = (btnRect.left + btnRect.width / 2) - left;
    tip.style.setProperty('--arrow-x', arrowX + 'px');
  }

  function showCarbonTooltip(btn, pinned) {
    const text = carbonDescriptionText(btn);
    const tip = getCarbonTooltip();
    if (!tip || !text) return;
    tip.querySelector('.carbonblock-tooltip__text').textContent = text;
    tip.hidden = false;
    if (carbonTooltipBtn && carbonTooltipBtn !== btn) {
      carbonTooltipBtn.setAttribute('aria-expanded', 'false');
    }
    carbonTooltipBtn = btn;
    btn.setAttribute('aria-expanded', 'true');
    if (pinned) carbonTooltipPinned = true;
    placeCarbonTooltip(btn, tip);
  }

  function hideCarbonTooltip() {
    carbonTooltipPinned = false;
    if (carbonTooltipBtn) carbonTooltipBtn.setAttribute('aria-expanded', 'false');
    carbonTooltipBtn = null;
    if (carbonTooltip) carbonTooltip.hidden = true;
  }

  function onCarbonInfoClick(event) {
    const btn = event.target.closest('.carbonblock-info');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    if (carbonTooltipPinned && carbonTooltipBtn === btn) {
      hideCarbonTooltip();
      return;
    }
    showCarbonTooltip(btn, true);
  }

  function onCarbonHoverIn(event) {
    const btn = event.target.closest('.carbonblock-info');
    if (!btn) return;
    showCarbonTooltip(btn, false);
  }

  function onCarbonHoverOut(event) {
    if (carbonTooltipPinned) return;
    const fromBtn = event.target.closest('.carbonblock-info');
    const fromTip = event.target.closest('.carbonblock-tooltip');
    if (!fromBtn && !fromTip) return;
    const next = event.relatedTarget;
    if (next && next.closest && (next.closest('.carbonblock-info') || next.closest('.carbonblock-tooltip'))) return;
    hideCarbonTooltip();
  }

  function mountAppBlocks(drawer) {
    if (!drawer) return;
    const slot = drawer.querySelector(APP_BLOCKS_SEL);
    enhanceCarbonBlocks(drawer);

    if (liveAppBlocks && liveAppBlocks.childElementCount && !drawer.contains(liveAppBlocks)) {
      if (slot) slot.replaceWith(liveAppBlocks);
      else {
        const panel = drawer.querySelector('.sidecart__panel');
        if (panel) panel.appendChild(liveAppBlocks);
      }
      syncAppBlocksPlacement(drawer);
      return;
    }

    if (slot && slot.childElementCount) {
      liveAppBlocks = slot;
      syncAppBlocksPlacement(drawer);
    }
  }

  /**
   * Re-fetches the sidecart section HTML, swaps it into the drawer,
   * re-inits the cross-sell carousel, and syncs the header count.
   */
  function refreshDrawer() {
    const el = getDrawer();
    const currentAppBlocks = el ? el.querySelector(APP_BLOCKS_SEL) : null;
    if (currentAppBlocks && currentAppBlocks.childElementCount) {
      liveAppBlocks = currentAppBlocks;
    }
    fetch(window.location.pathname + '?section_id=' + encodeURIComponent(getSectionId()))
      .then(function (res) { return res.text(); })
      .then(function (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newDrawer = doc.getElementById(DRAWER_ID);
        if (!el || !newDrawer) return;
        // Skeleton is for the initial server render only; on refreshes the
        // data is already in the new HTML, so showing it causes a flash on
        // every cart interaction.
        const xsell = newDrawer.querySelector('.cart-xsell');
        if (xsell) xsell.classList.remove('cart-xsell--loading');
        if (document.body.dataset.newSidecart === 'B') {
          const benefitAccordion = newDrawer.querySelector(BENEFIT_ACCORDION);
          if (benefitAccordion) benefitAccordion.setAttribute('open', '');
        }
        el.innerHTML = newDrawer.innerHTML;
        mountAppBlocks(el);
        initSwiper(el);
        updateHeaderCount();
      })
      .catch(function (err) {
        console.error('[sidecart] section refresh failed:', err);
      });
  }

  /**
   * Syncs the header cart icon by re-rendering the `cart-icon-bubble` section.
   * This swaps both the empty/non-empty SVG and the count bubble in one shot,
   * and works even when the bubble was removed (e.g. cart went empty then refilled).
   */
  function updateHeaderCount() {
    fetch(window.Shopify.routes.root + '?section_id=cart-icon-bubble')
      .then(function (res) { return res.text(); })
      .then(function (html) {
        const target = document.querySelector(TRIGGER_SEL);
        if (!target) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('.shopify-section');
        if (!fresh) return;
        target.innerHTML = fresh.innerHTML;
      })
      .then(function () { return fetch(window.Shopify.routes.root + 'cart.js'); })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count], .cart-count, .sidecart__count').forEach(function (el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function (err) {
        console.error('[sidecart] header count sync failed:', err);
      });
  }

  /**
   * Updates a line item: quantity change, removal (qty 0), or selling plan swap.
   * Shows per-item loading state while in-flight, then refreshes the drawer.
   */
  function cartChange(key, quantity, sellingPlan) {
    const el = getDrawer();
    const itemEl = el ? el.querySelector('[data-line-item-key="' + key + '"]') : null;
    if (itemEl) itemEl.classList.add('sidecart__item--loading');

    const body = { id: key, quantity: quantity };
    if (sellingPlan !== undefined) body.selling_plan = sellingPlan;
    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) { return res.json(); })
      .then(function () { refreshDrawer(); })
      .catch(function (err) {
        console.error('[sidecart] cart/change failed:', err);
        if (itemEl) itemEl.classList.remove('sidecart__item--loading');
      });
  }

  // ─── Subscriptions (selling plan) ─────────────────────────────────────────

  /**
   * Triggered by the subscription checkbox.
   * On check: applies the first non-empty selling plan from the select.
   * On uncheck: clears the selling plan (one-time purchase).
   */
  function handleSubscriptionToggle(checkbox) {
    const subscriptionEl = checkbox.closest('.sidecart__subscription');
    if (!subscriptionEl) return;
    const key    = subscriptionEl.dataset.key;
    const itemEl = subscriptionEl.closest('.sidecart__item');
    const qty    = getItemQty(itemEl || subscriptionEl);
    const select = subscriptionEl.querySelector('.sidecart__subscription-options');

    if (checkbox.checked) {
      const firstPlan = select
        ? Array.from(select.options).find(function (o) { return o.value !== ''; })
        : null;
      const planId = firstPlan ? firstPlan.value : '';
      if (planId) {
        select.value = planId;
        cartChange(key, qty, planId);
      }
    } else {
      cartChange(key, qty, '');
    }
  }

  /**
   * Triggered when the subscription frequency dropdown changes.
   * Swaps the active selling plan only if the checkbox is currently on.
   */
  function handleSubscriptionSelect(select) {
    const subscriptionEl = select.closest('.sidecart__subscription');
    if (!subscriptionEl) return;
    const key      = subscriptionEl.dataset.key;
    const itemEl   = subscriptionEl.closest('.sidecart__item');
    const qty      = getItemQty(itemEl || subscriptionEl);
    const checkbox = subscriptionEl.querySelector('.sidecart__subscription-toggle');
    const planId   = select.value;

    if (!planId) {
      if (checkbox) checkbox.checked = false;
      cartChange(key, qty, '');
    } else if (checkbox && checkbox.checked) {
      cartChange(key, qty, planId);
    }
  }

  /** Reads the current quantity from a line item's qty input (defaults to 1). */
  function getItemQty(itemEl) {
    const input = itemEl.querySelector('.sidecart__qty-input');
    return input ? parseInt(input.value, 10) || 1 : 1;
  }

  // ─── Promo / discount ─────────────────────────────────────────────────────

  // Delay (ms) before refreshing the drawer on a successful apply, so the
  // user sees the success message before the section is replaced.
  const PROMO_SUCCESS_REFRESH_MS = 2000;
  // Delay (ms) before auto-hiding an error feedback message (invalid code,
  // non-applicable code, network failure, empty input).
  const PROMO_ERROR_HIDE_MS = 2000;

  /** Cancels any pending auto-hide of the promo feedback. */
  function clearPromoFeedbackTimer() {
    if (promoFeedbackTimer) {
      clearTimeout(promoFeedbackTimer);
      promoFeedbackTimer = null;
    }
  }

  /** Resets the feedback element to a clean, hidden state. */
  function resetPromoFeedback(feedback) {
    clearPromoFeedbackTimer();
    if (!feedback) return;
    feedback.textContent = '';
    feedback.classList.remove('sidecart__promo-feedback--error', 'sidecart__promo-feedback--success');
    feedback.setAttribute('hidden', '');
  }

  /** Shows an error message in the feedback element and auto-hides it after PROMO_ERROR_HIDE_MS. */
  function showPromoError(feedback, message) {
    if (!feedback) return;
    clearPromoFeedbackTimer();
    feedback.textContent = message;
    feedback.classList.remove('sidecart__promo-feedback--success');
    feedback.classList.add('sidecart__promo-feedback--error');
    feedback.removeAttribute('hidden');
    promoFeedbackTimer = setTimeout(function () {
      promoFeedbackTimer = null;
      resetPromoFeedback(feedback);
    }, PROMO_ERROR_HIDE_MS);
  }

  /** Shows a success message in the feedback element. */
  function showPromoSuccess(feedback, message) {
    if (!feedback) return;
    clearPromoFeedbackTimer();
    feedback.textContent = message;
    feedback.classList.remove('sidecart__promo-feedback--error');
    feedback.classList.add('sidecart__promo-feedback--success');
    feedback.removeAttribute('hidden');
  }

  /**
   * Validates the promo input, posts the code to /cart/update.js, and shows
   * inline feedback (error or success). On success, briefly shows a confirmation
   * before refreshing the drawer so the user sees the result.
   */
  function handlePromoApply(fieldEl) {
    const inputEl  = fieldEl.querySelector('.sidecart__promo-input');
    const applyBtn = fieldEl.querySelector('.sidecart__promo-apply');
    const feedback = document.querySelector('.sidecart__promo-feedback');
    if (!inputEl) return;

    resetPromoFeedback(feedback);

    const code = (inputEl.value || '').trim().toUpperCase();
    if (!code) {
      showPromoError(feedback, 'Please enter a promo code.');
      return;
    }

    const originalLabel = applyBtn ? applyBtn.textContent : '';
    if (applyBtn) {
      applyBtn.disabled = true;
      applyBtn.textContent = 'Applying…';
    }
    inputEl.disabled = true;

    function restoreControls() {
      if (applyBtn) {
        applyBtn.disabled = false;
        applyBtn.textContent = originalLabel;
      }
      inputEl.disabled = false;
    }

    // Block the global `cartUpdated` listener until we finish showing feedback.
    // The fetch proxy emits cartUpdated on /cart/update.js, which would otherwise
    // refresh the drawer instantly and destroy the success message.
    promoRefreshPending = true;

    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: code }),
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        const matched = (cart.discount_codes || []).find(function (d) {
          return d.code.toUpperCase() === code;
        });
        const applied = matched && matched.applicable !== false;

        if (applied) {
          showPromoSuccess(feedback, 'Code “' + code + '” applied.');
          inputEl.value = '';
          // Keep controls disabled until the section refreshes — the refresh
          // replaces the markup, so no need to restore manually here.
          setTimeout(function () {
            promoRefreshPending = false;
            refreshDrawer();
          }, PROMO_SUCCESS_REFRESH_MS);
          return;
        }

        if (matched && matched.applicable === false) {
          showPromoError(feedback, 'This code can’t be applied to your cart.');
        } else {
          showPromoError(feedback, 'This promo code isn’t valid.');
        }
        promoRefreshPending = false;
        restoreControls();
      })
      .catch(function () {
        showPromoError(feedback, 'Unable to apply discount. Please try again.');
        promoRefreshPending = false;
        restoreControls();
      });
  }

  /** Clears any applied discount code and refreshes the drawer. */
  function handleDiscountRemove() {
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: '' }),
    })
      .then(function () { refreshDrawer(); })
      .catch(function (err) { console.error('[sidecart] discount remove failed:', err); });
  }

  /** Expands/collapses the promo code input field and focuses it once the transition ends. */
  function handlePromoToggle(btn) {
    const fieldId  = btn.getAttribute('aria-controls');
    const field    = document.getElementById(fieldId);
    if (!field) return;
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!expanded));
    if (expanded) {
      field.classList.remove('sidecart__promo-field--open');
    } else {
      field.classList.add('sidecart__promo-field--open');
      const input = field.querySelector('.sidecart__promo-input');
      if (input) setTimeout(function () { input.focus(); }, PROMO_FIELD_TRANSITION_MS);
    }
  }

  // ─── UI toggles (subtotal, SMS) ───────────────────────────────────────────

  /** Expands/collapses the subtotal breakdown and swaps the "Show/Hide" label. */
  function handleSubtotalToggle(btn) {
    const breakdownId = btn.getAttribute('aria-controls');
    const breakdown   = document.getElementById(breakdownId);
    if (!breakdown) return;
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!expanded));
    const label = btn.querySelector('span');
    if (expanded) {
      breakdown.setAttribute('hidden', '');
      if (label) label.textContent = 'Show Details';
    } else {
      breakdown.removeAttribute('hidden');
      if (label) label.textContent = 'Hide Details';
    }
  }

  /**
   * Toggles the SMS opt-in state with optimistic UI and persists the new
   * state as a cart attribute (`buyer_accepts_sms_marketing`). Reverts the
   * UI if the request fails so the toggle never lies about the cart state.
   */
  function handleSmsToggle(btn) {
    const wasChecked = btn.getAttribute('aria-checked') === 'true';
    const nowChecked = !wasChecked;

    btn.setAttribute('aria-checked', String(nowChecked));

    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          buyer_accepts_sms_marketing: nowChecked ? 'true' : 'false'
        }
      })
    })
      .catch(function (err) {
        console.error('[sidecart] SMS opt-in update failed:', err);
        btn.setAttribute('aria-checked', String(wasChecked));
      });
  }

  // ─── Gift threshold (Sidecart Gamification — Goal 2) ──────────────────────

  const GIFT_PROPERTY = '__free_gift';

  /**
   * Reads the gift config from the drawer's data attributes.
   * Returns null when the gift is not configured (variant id missing or
   * threshold <= 0), which acts as a clean kill switch.
   */
  function getGiftConfig() {
    const el = getDrawer();
    if (!el) return null;
    const variantId = (el.dataset.giftVariantId || '').trim();
    const threshold = parseInt(el.dataset.giftThreshold, 10);
    if (!variantId || !threshold || threshold <= 0) return null;
    return { variantId: variantId, threshold: threshold };
  }

  /**
   * Adds the free gift to the cart, tagged with __free_gift properties.
   * Does NOT refresh the drawer here — the global fetch Proxy emits
   * 'cartUpdated' on /cart/add, and our listener handles the refresh.
   */
  function addGiftToCart(variantId, thresholdCents) {
    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', 1);
    body.append('properties[' + GIFT_PROPERTY + ']', 'true');
    body.append('properties[__free_gift_cost]', String(thresholdCents));

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (res) { return res.json(); })
      .catch(function (err) { console.error('[sidecart] gift add failed:', err); });
  }

  /**
   * Verifies the cart subtotal against the gift threshold and toggles the
   * free gift line item accordingly:
   *   subtotal >= threshold && !hasGift  → add gift
   *   subtotal <  threshold &&  hasGift  → remove gift
   * Anti-loop: each branch flips hasGift on the next cartUpdated tick,
   * so the second pass falls through.
   */
  function checkGiftThreshold() {
    const config = getGiftConfig();
    if (!config) return;

    fetch(window.Shopify.routes.root + 'cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        const subtotal = cart.items_subtotal_price;
        const giftItem = (cart.items || []).find(function (item) {
          return String(item.id) === String(config.variantId)
            && item.properties
            && item.properties[GIFT_PROPERTY];
        });
        const hasGift = Boolean(giftItem);

        if (subtotal >= config.threshold && !hasGift) {
          addGiftToCart(config.variantId, config.threshold);
        } else if (subtotal < config.threshold && hasGift) {
          cartChange(giftItem.key, 0);
        }
      })
      .catch(function (err) { console.error('[sidecart] gift threshold check failed:', err); });
  }

  // ─── Event delegation ─────────────────────────────────────────────────────

  /** Delegated change handler for the subscription <select>. */
  function onDrawerChange(e) {
    const target = e.target;
    if (target.classList.contains('sidecart__subscription-options')) {
      handleSubscriptionSelect(target);
    }
  }

  /**
   * Single click handler for everything inside the drawer:
   * subscription toggle, backdrop close, close button, qty +/-, item remove,
   * subtotal/promo/SMS toggles, promo apply, discount remove.
   */
  function onDrawerClick(e) {
    const target = e.target;

    const toggleLabel = target.closest('label.sidecart__subscription-label');
    if (toggleLabel) {
      const checkbox = toggleLabel.querySelector('.sidecart__subscription-toggle');
      if (checkbox) {
        // Let the browser toggle first, then read the new state
        setTimeout(function () { handleSubscriptionToggle(checkbox); }, 0);
      }
      return;
    }

    if (target.classList.contains('sidecart__subscription-toggle')) {
      setTimeout(function () { handleSubscriptionToggle(target); }, 0);
      return;
    }

    // Backdrop close (click lands directly on the overlay, not inside the panel)
    if (target === getDrawer()) {
      close();
      return;
    }

    if (target.closest('.sidecart__close')) {
      close();
      return;
    }

    const removeBtn = target.closest('.sidecart__item-remove');
    if (removeBtn) {
      const removeItem = removeBtn.closest('.sidecart__item');
      if (removeItem && removeItem.dataset.lineItemKey) {
        cartChange(removeItem.dataset.lineItemKey, 0);
      }
      return;
    }

    const minusBtn = target.closest('.sidecart__qty-btn--minus');
    if (minusBtn) {
      const minusItem = minusBtn.closest('.sidecart__item');
      if (minusItem && minusItem.dataset.lineItemKey) {
        const qtyMinus = getItemQty(minusItem);
        cartChange(minusItem.dataset.lineItemKey, Math.max(0, qtyMinus - 1));
      }
      return;
    }

    const plusBtn = target.closest('.sidecart__qty-btn--plus');
    if (plusBtn) {
      const plusItem = plusBtn.closest('.sidecart__item');
      if (plusItem && plusItem.dataset.lineItemKey) {
        cartChange(plusItem.dataset.lineItemKey, getItemQty(plusItem) + 1);
      }
      return;
    }

    const subtotalBtn = target.closest('.sidecart__subtotal-details');
    if (subtotalBtn) {
      handleSubtotalToggle(subtotalBtn);
      return;
    }

    const promoBtn = target.closest('.sidecart__promo-btn');
    if (promoBtn) {
      handlePromoToggle(promoBtn);
      return;
    }

    const promoApply = target.closest('.sidecart__promo-apply');
    if (promoApply) {
      const fieldEl = promoApply.closest('.sidecart__promo-field');
      if (fieldEl) handlePromoApply(fieldEl);
      return;
    }

    const discountRemove = target.closest('.sidecart__detail-code-discount');
    if (discountRemove) {
      handleDiscountRemove();
      return;
    }

    const smsBtn = target.closest('.sidecart__sms-toggle');
    if (smsBtn) {
      handleSmsToggle(smsBtn);
      return;
    }
  }

  // ─── Init / bootstrap ─────────────────────────────────────────────────────

  /**
   * Wires up all listeners: delegated drawer events, header trigger,
   * ATC interception (page forms + product modal), x-sell add hook,
   * theme cart-update events, and Esc key.
   */
  function init() {
    drawerEl = document.getElementById(DRAWER_ID);
    if (!drawerEl) return;

    // Delegated handlers cover all panel interactions + backdrop click
    drawerEl.addEventListener('click', onDrawerClick);
    drawerEl.addEventListener('click', onCarbonInfoClick, true);
    drawerEl.addEventListener('click', function (event) {
      if (!carbonTooltipPinned) return;
      if (event.target.closest('.carbonblock-info') || event.target.closest('.carbonblock-tooltip')) return;
      hideCarbonTooltip();
    });
    drawerEl.addEventListener('mouseover', onCarbonHoverIn);
    drawerEl.addEventListener('mouseout', onCarbonHoverOut);
    drawerEl.addEventListener('focusin', onCarbonHoverIn);
    drawerEl.addEventListener('focusout', onCarbonHoverOut);
    drawerEl.addEventListener('change', onDrawerChange);

    const carbonObserver = new MutationObserver(function () {
      enhanceCarbonBlocks(drawerEl);
    });
    carbonObserver.observe(drawerEl, { childList: true, subtree: true });

    // Cart icon trigger
    document.addEventListener('click', function (e) {
      const trigger = e.target.closest(TRIGGER_SEL);
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      if (isOpen) {
        close();
      } else {
        refreshDrawer();
        open();
      }
    });

    // Quick-view modal only: the standard on-page PDP form's submit is owned
    // by product-form.js (ProductForm custom element), which calls
    // window.Sidecart.open()/.refresh() itself on a successful add — do not
    // also intercept it here, or the item gets added to the cart twice.
    document.addEventListener('submit', function (e) {
      const form = e.target.closest(ATC_FORM_SEL);
      if (!form || !form.classList.contains('product-modal-form')) return;

      // Let it submit natively (handled inline by the quick-view modal's own
      // script), then open the drawer once the modal closes (signaled by
      // removing the `.show` class).
      const modal = document.querySelector('product-modal');
      if (modal) {
        const observer = new MutationObserver(function (mutations, obs) {
          mutations.forEach(function (mutation) {
            if (mutation.attributeName === 'class' && !modal.classList.contains('show')) {
              obs.disconnect();
              refreshDrawer();
              open();
            }
          });
        });
        observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
      }
    });

    // Xsell add — fired by component-cart-xsell.js after a successful cart/add
    document.addEventListener('xsell:added', function () { refreshDrawer(); open(); });

    // Cart update events (CRO.md: DOM event "cartUpdated" + custom "cart:refresh")
    // Each cart mutation also re-checks the gift threshold (Goal 2).
    // Skip while a promo is showing its delayed-refresh feedback, otherwise the
    // drawer rebuild here would wipe the success message before its timer fires.
    document.addEventListener('cartUpdated', function () {
      if (promoRefreshPending) return;
      refreshDrawer();
      checkGiftThreshold();
    });
    document.addEventListener('cart:refresh', refreshDrawer);

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) close();
    });

    // Sync gift state on first load (cart may already cross the threshold)
    checkGiftThreshold();
    mountAppBlocks(drawerEl);
  }

  // Always boot — the sidecart is the theme's only cart drawer now.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.__initNewSidecart = window.__initNewSidecart || function () {};
  window.Sidecart = { open: open, close: close, refresh: refreshDrawer };
})();
